import type { QueryClient } from '@tanstack/react-query';
import { invalidateShareThumbnailCache } from './share-thumbnail-cache';

/**
 * Dependency invalidation carries no protected ids/content. Share changes can
 * affect other email rows, and visibility changes can remove prior audiences;
 * refetch all dependent views through their permission-checked endpoints.
 */
export function invalidateLinkedShareProjectionQueries(client: QueryClient): void {
  invalidateShareThumbnailCache(client);
  void client.invalidateQueries({
    predicate: query => {
      const root = query.queryKey[0];
      if (typeof root !== 'string') return false;
      return [
        '/api/tree/snapshot', '/api/tree/pulse', '/api/projects', '/api/years',
        '/api/pulses', '/api/preview-shares/by-pulse',
      ].some(prefix => root === prefix || root.startsWith(`${prefix}/`));
    },
  });
}

/** Persisted dependency instruction, never raw pulse metadata. The backend
 * uses meta.treeProjectionChanged for row events and identity-free tree events;
 * lifecycle updates retain their nested update form. */
export function isLinkedShareProjectionDelta(delta: {
  entityType?: string;
  mutationType?: string;
  data?: any;
  meta?: { treeProjectionChanged?: unknown };
}): boolean {
  return delta.meta?.treeProjectionChanged === true
    || delta.data?.treeProjectionChanged === true
    || (delta.entityType === 'pulse'
      && ['create', 'update', 'delete'].includes(delta.mutationType || '')
      && delta.data?.update?.treeProjectionChanged === true);
}